import React, { useState } from "react";
import ReactDOMServer from "react-dom/server";
import { usePdfGenerator } from "hooks/usePdfGenerator";
import MarksheetTemplate from "components/templates/MarksheetTemplate";
import { useMarksheetTypeOptions } from "components/enum/MarksheetTypeOptions";
import { useMarkSheets } from "hooks/useMarkSheets";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  IconButton,
} from "@mui/material";
import { useSessionClass } from "contexts/SessionClassContext";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { useSchools } from "hooks/useSchools";
import { useTranslation } from "react-i18next";

const ResultManagement: React.FC = () => {
  const { t } = useTranslation();
  const { selectedClass, selectedClassName } = useSessionClass();
  const marksheetTypeOptions = useMarksheetTypeOptions();
  const [open, setOpen] = useState(true);
  const [marksheetType, setMarksheetType] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<any>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { useSchoolById } = useSchools();
  const { data: school, isLoading: schoolLoading } = useSchoolById(
    typeof user?.schoolId === "number" ? user.schoolId : undefined
  );

  // Get the class name with section from selectedClass
  const fullClassName = selectedClass
    ? `${selectedClass.name} ${selectedClass.section}`
    : "";

  const { useMarksheetByTypeClassRoll } = useMarkSheets();
  const { data, error, refetch, isFetching } = useMarksheetByTypeClassRoll(
    marksheetType,
    fullClassName, // Using the full class name with section
    rollNo,
    false // don't auto-fetch
  );

  const { generatePdf, isGenerating, error: pdfError } = usePdfGenerator();

  const handleSubmit = async () => {
    if (!selectedClass) {
      setFormError(t("no_class_selected"));
      return;
    }

    setFormError(null);
    try {
      const result = await refetch();
      if (result.error) {
        // AxiosError ka message priority pe, warna generic error
        setFormError(
          (result.error as any)?.response?.data?.message ||
          result.error.message ||
          t("something_went_wrong")
        );
      } else if (result.data) {
        setFormValues(result.data);
        setOpen(false);
      }
    } catch (err: any) {
      setFormError(err?.message || t("something_went_wrong"));
    }
  };

  const handlePrintPDF = () => {
    if (!formValues) return;

    const htmlString = ReactDOMServer.renderToString(
      <MarksheetTemplate
        {...formValues}
        schoolName={
          schoolLoading ? "Loading..." : school?.nameofSchool || formValues.schoolName
        }
        onPrint={undefined}
        isPdf={true}
      />
    );

    generatePdf({
      htmlContent: htmlString,
      fileName: `${formValues.student.firstName}_${formValues.student.rollNumber}_${formValues.marksheetType}_${formValues.sessionYear}_Marksheet.pdf`,
    });
  };

  // Helper to check if selected class is 10th or 12th
  const isBoardClass =
    selectedClass &&
    (selectedClass.name === "10th" || selectedClass.name === "12th");

  // Filter marksheet type options
  let filteredMarksheetTypeOptions: { value: string; label: string }[] = [];
  if (isBoardClass) {
    filteredMarksheetTypeOptions = marksheetTypeOptions.filter(
      (opt) => opt.value !== "ANNUAL"
    );
  } else if (selectedClass?.name) {
    // For all other classes (e.g., 9th, 11th, etc.), exclude BOARD and PRE_BOARD
    filteredMarksheetTypeOptions = marksheetTypeOptions.filter(
      (opt) => opt.value !== "BOARD" && opt.value !== "PRE_BOARD"
    );
  } else {
    filteredMarksheetTypeOptions = marksheetTypeOptions;
  }

  // Auto-select the first available option for non-board classes
  React.useEffect(() => {
    if (!isBoardClass && filteredMarksheetTypeOptions.length > 0) {
      setMarksheetType(filteredMarksheetTypeOptions[0].value);
    }
    // eslint-disable-next-line
  }, [isBoardClass, selectedClass?.name]);

  // If no class is selected, show an error message
  if (!selectedClass) {
    return (
      <Alert severity="warning" style={{ margin: "20px" }}>
        {t("please_select_a_class_first_to_view_marksheets")}
      </Alert>
    );
  }

  const handleClose = () => {
    setOpen(false);
    navigate("/teacher/dashboard");
  };

  return (
    <>
      <Dialog open={open} disableEscapeKeyDown>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("get_marksheet")}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            label={t("class")}
            value={fullClassName}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          <TextField
            select
            label={t("marksheet_type")}
            value={marksheetType}
            onChange={(e) => setMarksheetType(e.target.value)}
            fullWidth
            margin="normal"
          >
            {filteredMarksheetTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("roll_no")}
            value={rollNo}
            onChange={(e) => setRollNo(e.target.value)}
            fullWidth
            margin="normal"
          />
          {formError && <Alert severity="error">{formError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmit}
            disabled={!marksheetType || !rollNo || isFetching}
            variant="contained"
            color="primary"
          >
            {t("submit")}
          </Button>
        </DialogActions>
      </Dialog>
      {formValues && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "start",
            gap: 8,
            width: "100%",
            minHeight: "60vh", // optional, for vertical centering
          }}
        >
          <MarksheetTemplate
            {...formValues}
            schoolName={
              schoolLoading
                ? "Loading..."
                : school?.nameofSchool || formValues.schoolName
            }
            onPrint={handlePrintPDF}
          />
          <IconButton
            onClick={() => {
              setFormValues(null);
              setOpen(true);
            }}
            size="small"
            style={{ marginTop: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </>
  );
};

export default ResultManagement;
